/**
 * Approach Section - Our Methodology & Expertise
 *
 * Research-driven explanation of our systematic approach
 * Background: Dark section with professional authority
 */

import { SectionHeading, Muted } from "@/components/ui/section-heading";

const ApproachSection = () => {
  return (
    <section className="relative bg-white/90 py-16 sm:py-24 lg:py-32 overflow-hidden shadow-2xl rounded-3xl">
      {/* CONTENT CONTAINER */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
        {/* SECTION HEADER */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <SectionHeading
            variant="question"
            className="mb-4 sm:mb-6 text-center"
          >
            Our Approach is Operational Intelligence & Automation
          </SectionHeading>
          <SectionHeading
            variant="headline"
            className="mb-8 sm:mb-12 lg:mb-16 text-center"
          >
            Proven principles and software
            <br />
            <Muted>to scale what creates leverage.</Muted>
          </SectionHeading>
        </div>

        {/* TWO-COLUMN LAYOUT */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* LEFT COLUMN: FOUR-PHASE METHODOLOGY */}
          <div className="order-2 lg:order-1 space-y-5">
            {/* PHASE 1: AUDIT */}
            <div className="bg-slate-50/90 rounded-xl p-5 border-2 border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-[#B87333]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900">1. Audit</h3>
              </div>

              <p className="text-slate-600 font-semibold leading-relaxed mb-4 text-sm">
                Identify inefficiencies and hidden costs in your current
                processes. We map your entire operational workflow to uncover
                bottlenecks, redundancies, and waste that silently erode
                profitability.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    Process mapping
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    Cost analysis
                  </p>
                </div>
              </div>
            </div>

            {/* PHASE 2: REDESIGN */}
            <div className="bg-slate-50/90 rounded-xl p-5 border-2 border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-[#B87333]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  2. Optimize & Simplify
                </h3>
              </div>

              <p className="text-slate-600 font-semibold leading-relaxed mb-4 text-sm">
                We optimize and simplify workflows by designing systems that do
                tedious work that allows your skilled workers to be leveraged
                correctly, instead of wasting time on time-consuming,
                low-leverage, but necessary tasks. The result is fewer errors,
                less friction, and processes that are easier to run, easier to
                scale, and easier to trust.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    System-led decision consistency
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    Simplified, automation-ready workflows
                  </p>
                </div>
              </div>
            </div>

            {/* PHASE 3: AUTOMATE */}
            <div className="bg-slate-50/90 rounded-xl p-5 border-2 border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-[#B87333]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  3. Automate
                </h3>
              </div>

              <p className="text-slate-600 font-semibold leading-relaxed mb-4 text-sm">
                Deploy AI and automation to handle repetitive, high-volume
                tasks. We implement intelligent systems that execute your
                optimized processes at machine speed—making smart decisions and
                providing real-time insights.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    AI decision support
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    System integration
                  </p>
                </div>
              </div>
            </div>

            {/* PHASE 4: SCALE */}
            <div className="bg-slate-50/90 rounded-xl p-5 border-2 border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-[#B87333]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900">4. Scale</h3>
              </div>

              <p className="text-slate-600 font-semibold leading-relaxed mb-4 text-sm">
                Expand operations profitably with systems that grow with you.
                Your optimized, automated processes allow you to 2-3x revenue
                without proportional cost increases—finally breaking the
                profitability paradox.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    Scalable infrastructure
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <svg
                    className="w-4 h-4 text-[#B87333] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-slate-900">
                    Performance monitoring
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: TRINITY IMAGE (shown first on mobile) */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-32">
            <div className="bg-white/90 rounded-2xl p-5 sm:p-8 border-2 border-slate-200 shadow-2xl">
              <img
                src="/ai-trinity.png"
                alt="AI + Domain Expert + Technologist = 500X multiplier effect"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="mt-6 text-center">
                <p className="text-slate-900 font-bold text-lg mb-2">
                  The Multiplier Effect
                </p>
                <p className="text-slate-600 font-semibold text-sm leading-relaxed">
                  When AI, domain expertise, and technical implementation
                  converge, the impact isn't additive—it's exponential.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;
